import React from "react";
import {
    FaCog,
    FaFlask,
    FaBolt,
    FaArrowRight,
    FaLightbulb,
    FaWeightHanging,
    FaCubes,
    FaTachometerAlt
} from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import SEO from "../components/SEO";

const Technology = () => {
    const { t } = useTranslation();

    // Colors for consistent theming
    const primaryTextColor = "text-[#212529]"; // Dark text
    const secondaryTextColor = "text-[#212529]/80"; // Medium gray
    const tertiaryTextColor = "text-[#212529]/60"; // Light gray

    const [heroRef, heroInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [developmentRef, developmentInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [benefitsRef, benefitsInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [designRef, designInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    const [ctaRef, ctaInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    return (
        <div className="min-h-screen font-sans overflow-hidden relative">
            <SEO 
                title="Technology - Axialis Axial Flux Motors"
                description="Axialis technology features yokeless stator with dual rotors, SMC stator cores, and integrated direct liquid cooling for superior torque density."
                keywords="axial flux technology, yokeless stator, dual rotors, SMC, Soft Magnetic Composite, concentrated windings, rectangular copper wire, direct liquid cooling, electric motor efficiency"
                path="/technology"
            />
            {/* Hero Section */}
            <section
                ref={heroRef}
                className="relative pt-32 pb-8"
            >
                <div className="container mx-auto px-4">
                    <div
                        className={`max-w-3xl mx-auto text-center relative z-20 transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${primaryTextColor}`}>
                            Technology
                        </h1>
                        <div className="h-0.5 w-16 bg-primary mx-auto mb-6"></div>
                        <p className={`text-lg md:text-xl ${secondaryTextColor} mb-10 leading-relaxed`}>
                            Through continuous development, Axialis seeks to refine axial flux motor technology — improving not only performance and efficiency, but also cooling capabilities and system integration.
                        </p>
                    </div>
                </div>
            </section>

            {/* Development Section */}
            <section
                ref={developmentRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className={`flex flex-col md:flex-row items-center max-w-6xl mx-auto gap-8 transition-all duration-1000 transform ${developmentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="w-full md:w-1/4 flex justify-center">
                            <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
                                <FaFlask className="text-primary text-5xl" />
                            </div>
                        </div>
                        <div className="w-full md:w-3/4">
                            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                                <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Development</h2>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg mb-4`}>
                                    While most electric vehicles today still rely on radial flux motors—a concept originating over 200 years ago—Axialis is focused on bringing a modern alternative to the market.
                                </p>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                                    Our work in axial flux technology introduces a more compact, efficient, and high-performance solution tailored to the evolving demands of electric mobility and beyond.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What Axial Flux Technology Offers Section */}
            <section
                ref={benefitsRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className={`flex flex-col md:flex-row-reverse items-center max-w-6xl mx-auto gap-8 transition-all duration-1000 transform ${benefitsInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="w-full md:w-1/4 flex justify-center">
                            <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
                                <FaBolt className="text-primary text-5xl" />
                            </div>
                        </div>
                        <div className="w-full md:w-3/4">
                            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                                <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>What Axial Flux Technology Offers</h2>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg mb-6`}>
                                    Axial flux motors provide several key advantages over traditional radial flux designs:
                                </p>
                                <ul className="space-y-2 mb-6">
                                    <li className="flex items-start">
                                        <span className="h-2 w-2 bg-primary rounded-full mt-2.5 mr-2 flex-shrink-0"></span>
                                        <span className={`${secondaryTextColor} text-lg`}>Higher torque density</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="h-2 w-2 bg-primary rounded-full mt-2.5 mr-2 flex-shrink-0"></span>
                                        <span className={`${secondaryTextColor} text-lg`}>Greater energy efficiency</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="h-2 w-2 bg-primary rounded-full mt-2.5 mr-2 flex-shrink-0"></span>
                                        <span className={`${secondaryTextColor} text-lg`}>Lower mass and volume</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="h-2 w-2 bg-primary rounded-full mt-2.5 mr-2 flex-shrink-0"></span>
                                        <span className={`${secondaryTextColor} text-lg`}>A space-efficient design well-suited for integration into modern electric drivetrains</span>
                                    </li>
                                </ul>
                                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                                    This technology represents a significant step forward in the evolution of electric propulsion systems.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Benefits of Our Design Section */}
            <section
                ref={designRef}
                className="py-16"
            >
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className={`text-3xl font-medium mb-4 ${primaryTextColor}`}>
                            Key Benefits of Our Design
                        </h2>
                        <div className="h-0.5 w-16 bg-primary mx-auto"></div>
                    </div>

                    <div className={`max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 transition-all duration-1000 transform ${designInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        {/* Benefit 1 */}
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40 h-full">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                                    <FaCubes className="text-primary text-2xl" />
                                </div>
                                <h3 className={`text-xl font-medium ${primaryTextColor}`}>Yokeless stator with dual rotors</h3>
                            </div>
                            <p className={`${secondaryTextColor} leading-relaxed`}>
                                Reduces the overall weight and volume of the motor
                            </p>
                        </div>

                        {/* Benefit 2 */}
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40 h-full">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                                    <FaLightbulb className="text-primary text-2xl" />
                                </div>
                                <h3 className={`text-xl font-medium ${primaryTextColor}`}>Stator cores made from SMC material</h3>
                            </div>
                            <p className={`${secondaryTextColor} leading-relaxed`}>
                                Enables cost-effective and precise manufacturing with improved magnetic properties
                            </p>
                        </div>

                        {/* Benefit 3 */}
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40 h-full">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                                    <FaWeightHanging className="text-primary text-2xl" />
                                </div>
                                <h3 className={`text-xl font-medium ${primaryTextColor}`}>Concentrated windings with rectangular copper wire</h3>
                            </div>
                            <p className={`${secondaryTextColor} leading-relaxed`}>
                                Maximizes fill factor and reduces copper mass for enhanced efficiency
                            </p>
                        </div>

                        {/* Benefit 4 */}
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40 h-full">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                                    <FaCog className="text-primary text-2xl" />
                                </div>
                                <h3 className={`text-xl font-medium ${primaryTextColor}`}>Optional full integration into electric drivetrains</h3>
                            </div>
                            <p className={`${secondaryTextColor} leading-relaxed`}>
                                Simplifies packaging and improves overall system performance
                            </p>
                        </div>

                        {/* Benefit 5 */}
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40 h-full md:col-span-2">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                                    <FaTachometerAlt className="text-primary text-2xl" />
                                </div>
                                <h3 className={`text-xl font-medium ${primaryTextColor}`}>Integrated direct liquid cooling</h3>
                            </div>
                            <p className={`${secondaryTextColor} leading-relaxed`}>
                                Achieves exceptional torque density and thermal performance under high loads
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section
                ref={ctaRef}
                className="py-20"
            >
                <div className="container mx-auto px-4 text-center">
                    <div
                        className={`max-w-3xl mx-auto transition-all duration-1000 transform ${ctaInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                            <h3 className={`text-2xl md:text-3xl font-medium mb-4 ${primaryTextColor}`}>
                                Ready to Experience Advanced Motor Technology?
                            </h3>
                            <p className={`${secondaryTextColor} mb-8`}>
                                Discover how our innovative axial flux technology can transform your application.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Link
                                    to="/contact"
                                    className="bg-primary/90 hover:bg-primary text-white backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Contact Us
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                                <Link
                                    to="/explore-motor"
                                    className="bg-white/70 hover:bg-white/80 text-[#212529] backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Explore Our Motor
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                            </div>
                            <p className={`${secondaryTextColor} mt-6 text-sm`}>
                                New to the topic? Read our explainer:{" "}
                                <Link to="/axial-flux-vs-radial-flux" className="underline hover:opacity-70">
                                    Axial flux vs radial flux motors — what's the difference?
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Technology; 